import * as THREE from 'three';
import { BODIES, BODY_BY_ID, type BodyDef } from './bodies';
import {
  bodyEquatorFrame,
  bodyOrientation,
  daysSinceJ2000,
  DEG,
  EARTH_MOON_MASS_RATIO,
  keplerPosition,
  lunarPosition,
} from './ephemeris';

export type BodyState = {
  def: BodyDef;
  /** heliocentric ecliptic position, metres (float64) */
  position: THREE.Vector3;
  /** body-fixed → ecliptic rotation */
  rotation: THREE.Matrix4;
  quaternion: THREE.Quaternion;
  equatorFrame: THREE.Matrix4;
  /** position change during the last update */
  delta: THREE.Vector3;
  /** rotation change during the last update (ecliptic frame) */
  deltaRotation: THREE.Quaternion;
};

/** Computes every body's position and orientation for a given UTC time. */
export class Universe {
  readonly bodies: BodyState[];
  readonly byId = new Map<string, BodyState>();
  timeMs = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly prevQ = new THREE.Quaternion();
  private readonly emBary = new THREE.Vector3();

  constructor() {
    this.bodies = BODIES.map((def) => {
      const state: BodyState = {
        def,
        position: new THREE.Vector3(),
        rotation: new THREE.Matrix4(),
        quaternion: new THREE.Quaternion(),
        equatorFrame: bodyEquatorFrame(def, new THREE.Matrix4()),
        delta: new THREE.Vector3(),
        deltaRotation: new THREE.Quaternion(),
      };
      this.byId.set(def.id, state);
      return state;
    });
  }

  get(id: string): BodyState {
    const b = this.byId.get(id);
    if (!b) throw new Error(`unknown body ${id}`);
    return b;
  }

  update(timeMs: number): void {
    const first = this.timeMs === 0;
    this.timeMs = timeMs;
    const d = daysSinceJ2000(timeMs);
    const T = d / 36525;

    // Earth–Moon: JPL elements give the barycentre.
    const earth = this.get('earth');
    const moon = this.get('moon');
    keplerPosition(earth.def.kepler!, T, this.emBary);

    for (const body of this.bodies) {
      const prev = this.tmp.copy(body.position);
      const next = new THREE.Vector3();
      const def = body.def;
      if (def.id === 'sun') {
        next.set(0, 0, 0);
      } else if (def.id === 'earth') {
        next.copy(this.emBary).addScaledVector(lunarPosition(T, new THREE.Vector3()), -EARTH_MOON_MASS_RATIO);
      } else if (def.lunar) {
        continue; // placed after Earth below
      } else if (def.kepler) {
        keplerPosition(def.kepler, T, next);
      } else if (def.moonOrbit && def.parent) {
        const parent = this.get(def.parent);
        const o = def.moonOrbit;
        const L = (o.L0 + (360 / o.periodDays) * d) * DEG;
        next.set(Math.cos(L) * o.radius, Math.sin(L) * o.radius, 0).applyMatrix4(parent.equatorFrame).add(parent.position);
      }
      body.delta.subVectors(next, prev);
      body.position.copy(next);
    }

    const moonPos = lunarPosition(T, new THREE.Vector3());
    const moonNext = moonPos.add(earth.position);
    moon.delta.subVectors(moonNext, moon.position);
    moon.position.copy(moonNext);

    for (const body of this.bodies) {
      this.prevQ.copy(body.quaternion);
      bodyOrientation(body.def, d, body.rotation);
      body.quaternion.setFromRotationMatrix(body.rotation);
      // deltaRotation = q_now * q_prev^-1
      body.deltaRotation.copy(body.quaternion).multiply(this.prevQ.invert());
      if (first) {
        body.delta.set(0, 0, 0);
        body.deltaRotation.identity();
      }
    }
  }

  /** Body whose sphere of influence contains the point, preferring the smallest. */
  dominantBody(pos: THREE.Vector3): BodyState {
    let best = this.get('sun');
    let bestSoi = Infinity;
    for (const b of this.bodies) {
      if (b.def.id === 'sun') continue;
      const dist = b.position.distanceTo(pos);
      if (dist < b.def.soi && b.def.soi < bestSoi) {
        best = b;
        bestSoi = b.def.soi;
      }
    }
    return best;
  }

  /** Nearest body by distance to surface; returns [body, altitude]. */
  nearestSurface(pos: THREE.Vector3): { body: BodyState; altitude: number } {
    let body = this.bodies[0];
    let altitude = Infinity;
    for (const b of this.bodies) {
      const alt = b.position.distanceTo(pos) - b.def.radius;
      if (alt < altitude) {
        altitude = alt;
        body = b;
      }
    }
    return { body, altitude };
  }

  /** Converts body-fixed lat/lon/alt to heliocentric position. */
  surfacePoint(body: BodyState, latDeg: number, lonDeg: number, alt: number, out: THREE.Vector3): THREE.Vector3 {
    const lat = latDeg * DEG, lon = lonDeg * DEG;
    const r = body.def.radius + alt;
    return out
      .set(Math.cos(lat) * Math.cos(lon) * r, Math.cos(lat) * Math.sin(lon) * r, Math.sin(lat) * r)
      .applyMatrix4(body.rotation)
      .add(body.position);
  }

  static readonly defs = BODY_BY_ID;
}
